// Job domain *************************************************************************************
// pathname: /v1/domain/detect
// ************************************************************************************************
import { useSwrApi } from '@hooks/useSwrApi';
import { ApiRequestBody } from '@type/api/apiUtilityFn';

/** Props */
interface UseSwrJobDomainProps {
  locale: Locale,
  payload: ApiRequestBody<'/v1/domain/detect', 'post'>,
  shouldFetch?: boolean,
  successCallback?: (data: ApiSchema<'AssignedDomain'>) => void,
  errorCallBack?: () => void,
  onDataChanged?: (data?: ApiSchema<'AssignedDomain'>) => void,
}

/**
 * @description SWR: fetch job domain.
 * - `locale` - the current locale
 * - `payload` - the payload
 * - `shouldFetch` - condition for fetching the data. Default true.
 * - `successCallback` - success callback; works only on successful fetch
 * - `errorCallBack` - error callback; works only on error fetch
 * - `onDataChanged` - data changed callback; works on every data change (from cache or fetch)
 */
export const useSwrJobDomain = (props: UseSwrJobDomainProps) => {
  const {
    locale,
    payload,
    shouldFetch = true,
    successCallback,
    errorCallBack,
    onDataChanged,
  } = props;

  return useSwrApi<ApiSchema<'AssignedDomain'>>({
    url: '/v1/domain/detect',
    locale,
    shouldFetch,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      payload,
    },
    apiOptions: {
      revalidateOnFocus: false,
      revalidateOnMount: false,
      keepPreviousData: true,
      dedupingInterval: 1000 * 60, // 1 minute
      onSuccess: (response) => {
        if (successCallback) successCallback(response);
      },
      onError: () => {
        if (errorCallBack) errorCallBack();
      },
    },
    onDataChange: (data) => {
      if (onDataChanged) onDataChanged(data);
    },
  });
};
